export default function (Alpine) {
    Alpine.data('jobsTable', (all_jobs, jobsPerPage = 15) => ({
        loading: false,
        cacheExpiresAt: null,
        jobs: all_jobs,
        jobsPerPage: jobsPerPage,
        currentPage: 1,
        visibleFilters: null,
        query: '',
        activities: [],
        levels: [],
        contracts: [],
        cities: [],
        filteredJobs: [],

        get pagesBetweenFirstAndLast() {
            if (this.totalPages <= 2) {
                return []
            }
            return Array.from({ length: this.totalPages - 2 }, (_, i) => i + 2)
        },

        get totalPages() {
            return Math.ceil(this.getFilteredJobs().length / this.jobsPerPage)
        },

        get previousPage() {
            return this.currentPage - 1 > 0 ? this.currentPage - 1 : null
        },

        get nextPage() {
            return this.currentPage + 1 <= this.totalPages ? this.currentPage + 1 : null
        },

        get allFilters() {
            return [
                this.query,
                ...this.activities,
                ...this.levels,
                ...this.contracts,
                ...this.cities,
            ]
        },

        get selectedFilters() {
            return this.allFilters.filter(filter => filter)
        },

        get visibleJobs() {
            // get the start and end index
            const start = (this.currentPage - 1) * this.jobsPerPage
            const end = start + this.jobsPerPage

            // return this.getFilteredJobs().slice(start, end)
            return this.filteredJobs.slice(start, end)
        },

        get queryParams() {
            const params = new URLSearchParams()
            if (this.query) {
                params.append('query', this.query)
            }
            if (this.activities.length) {
                params.append('activities', this.activities.join(','))
            }
            if (this.levels.length) {
                params.append('levels', this.levels.join(','))
            }
            if (this.contracts.length) {
                params.append('contracts', this.contracts.join(','))
            }
            if (this.cities.length) {
                params.append('cities', this.cities.join(','))
            }
            return params.toString()
        },

        get availableActivities() {
            if (this.visibleFilters !== 'activities') {
                return []
            }

            return Array.from(this.jobs.map(job => job.firstactivity))
                .filter((v, i, a) => a.indexOf(v) === i) // Unique values
                .map(active => {
                    return {
                        name: active,
                        count: Array.from(this.getFilteredJobs('activities').map(job => job.firstactivity))
                            .flat()
                            .filter(a => a === active).length
                    }
                })
                .filter(active => active.count > 0)
                // Sort by alphabet. Ignore special characters like ( and )
                .sort((a, b) => a.name.localeCompare(b.name, 'de', { ignorePunctuation: true }))
        },

        get availableLevels() {
            if (this.visibleFilters !== 'levels') {
                return []
            }

            return Array.from(this.jobs.map(job => job.shifttype))
                .filter((v, i, a) => a.indexOf(v) === i) // Unique values
                .map(level => {
                    return {
                        name: level,
                        count: Array.from(this.getFilteredJobs('levels').map(job => job.shifttype))
                            .flat()
                            .filter(l => l === level).length
                    }
                })
                .filter(level => level.count > 0)
                // Sort by alphabet. Ignore special characters like ( and )
                .sort((a, b) => a.name.localeCompare(b.name, 'de', { ignorePunctuation: true }))
        },

        get availableContracts() {
            if (this.visibleFilters !== 'contracts') {
                return []
            }

            return Array.from(this.jobs.map(job => job.contract))
                .filter((v, i, a) => a.indexOf(v) === i) // Unique values
                .map(contract => {
                    return {
                        name: contract,
                        count: Array.from(this.getFilteredJobs('contracts').map(job => job.contract))
                            .flat()
                            .filter(c => c === contract).length
                    }
                })
                .filter(contract => contract.count > 0)
                // Sort by alphabet. Ignore special characters like ( and )
                .sort((a, b) => a.name.localeCompare(b.name, 'de', { ignorePunctuation: true }))
        },

        get availableCities() {
            if (this.visibleFilters !== 'cities') {
                return []
            }

            return Array.from(this.jobs.map(job => job.city))
                .filter((v, i, a) => a.indexOf(v) === i) // Unique values
                .map(city => {
                    return {
                        name: city,
                        count: Array.from(this.getFilteredJobs('cities').map(job => job.city))
                            .flat()
                            .filter(c => c === city).length
                    }
                })
                .filter(city => city.count > 0)
                // Sort by alphabet. Ignore special characters like ( and )
                .sort((a, b) => a.name.localeCompare(b.name, 'de', { ignorePunctuation: true }))
        },

        getFilteredJobs(excludeFilter = null) {
            let filtered_jobs = this.jobs

            if (this.query) {
                // Get all jobs that contain the query in the title, description or location
                filtered_jobs = filtered_jobs.filter(job => {
                    return job.title.toLowerCase().includes(this.query.toLowerCase())
                        || job.id.toLowerCase().includes(this.query.toLowerCase())
                        || job.city.toLowerCase().includes(this.query.toLowerCase())
                        || job.searchable_words.includes(this.query.toLowerCase())
                        || this.activities.includes(job.firstactivity)
                        || this.activities.includes(job.secondactivity)
                })
            }

            if (excludeFilter !== 'activities' && this.activities.length) {
                filtered_jobs = filtered_jobs.filter(job => {
                    return this.activities.includes(job.firstactivity) || this.activities.includes(job.secondactivity)
                })
            }

            if (excludeFilter !== 'levels' && this.levels.length) {
                filtered_jobs = filtered_jobs.filter(job => this.levels.includes(job.shifttype))
            }

            if (excludeFilter !== 'contracts' && this.contracts.length) {
                filtered_jobs = filtered_jobs.filter(job => this.contracts.includes(job.contract))
            }

            if (excludeFilter !== 'cities' && this.cities.length) {
                filtered_jobs = filtered_jobs.filter(job => this.cities.includes(job.city))
            }

            return filtered_jobs
        },

        updateFilteredJobs() {
            this.filteredJobs = this.getFilteredJobs()
        },

        removeActivity(active) {
            this.activities = this.activities.filter(a => a !== active)
        },

        removeLevel(level) {
            this.levels = this.levels.filter(l => l !== level)
        },

        removeContract(contract) {
            this.contracts = this.contracts.filter(c => c !== contract)
        },

        removeCity(city) {
            this.cities = this.cities.filter(c => c !== city)
        },

        resetAllFilters() {
            this.query = ''
            this.activities = []
            this.levels = []
            this.contracts = []
            this.cities = []
        },

        goToPage(page) {
            this.currentPage = parseInt(page)

            const url = new URL(window.location);
            url.searchParams.set('page', page);
            url.searchParams.set('query', this.query);
            url.searchParams.set('cities', this.cities.map(city => city.replace(',', '%2C')).join(','));
            url.searchParams.set('levels', this.levels.map(level => level.replace(',', '%2C')).join(','));

            window.history.replaceState({}, '', url);
        },

        setCacheTimer() {
            if (this.cacheExpiresAt === null) {
                return
            }

            const currentTimestamp = Math.floor(Date.now() / 1000)
            const secondsLeft = this.cacheExpiresAt - currentTimestamp

            setTimeout(() => {
                fetch('/cache-jobs')
                    .then(response => response.json())
                    .then(data => {
                        this.cacheExpiresAt = data.cache_expires_at
                        this.setCacheTimer()
                    })
                    .catch(error => {
                        console.error('Error fetching cache:', error)
                        this.cacheExpiresAt = null
                        this.setCacheTimer()
                    });
            }, secondsLeft * 1000)
        },

        init() {
            // Get the current page from the URL query parameter
            const urlParams = new URLSearchParams(window.location.search);

            // const page = urlParams.get('page') || 1
            this.currentPage = urlParams.get('page') ? parseInt(urlParams.get('page')) : 1

            // Get the query and cities from the URL query parameters
            this.query = urlParams.get('query') || ''
            this.activities = urlParams.get('activities') ? urlParams.get('activities').split(',') : []
            this.levels = urlParams.get('levels') ? urlParams.get('levels').split(',') : []
            this.contracts = urlParams.get('contracts') ? urlParams.get('contracts').split(',') : []
            this.cities = urlParams.get('cities') ? urlParams.get('cities').split(',') : []

            fetch('/cache-jobs')
                .then(response => response.json())
                .then(data => {
                    this.cacheExpiresAt = data.cache_expires_at
                    this.setCacheTimer()
                })

            this.$watch('query', () => {
                const url = new URL(window.location);
                url.searchParams.set('query', this.query)
                window.history.replaceState({}, '', url)
                this.updateFilteredJobs()
                this.goToPage(1)
            });

            this.$watch('activities', () => {
                const url = new URL(window.location)
                url.searchParams.set('activities', this.activities.map(active => active.replace(',', '%2C')).join(','))
                window.history.replaceState({}, '', url)
                this.updateFilteredJobs()
                this.goToPage(1)
            });

            this.$watch('levels', () => {
                const url = new URL(window.location)
                url.searchParams.set('levels', this.levels.map(level => level.replace(',', '%2C')).join(','))
                window.history.replaceState({}, '', url)
                this.updateFilteredJobs()
                this.goToPage(1)
            });

            this.$watch('contracts', () => {
                const url = new URL(window.location)
                url.searchParams.set('contracts', this.contracts.map(contract => contract.replace(',', '%2C')).join(','))
                window.history.replaceState({}, '', url)
                this.updateFilteredJobs()
                this.goToPage(1)
            });

            this.$watch('cities', () => {
                const url = new URL(window.location);
                url.searchParams.set('cities', this.cities.map(city => city.replace(',', '%2C')).join(','))
                window.history.replaceState({}, '', url)
                this.updateFilteredJobs()
                this.goToPage(1)
            });

            window.addEventListener('popstate', () => {
                const urlParams = new URLSearchParams(window.location.search)
                const page = urlParams.get('page') || 1
                this.query = urlParams.get('query') || ''
                this.activities = urlParams.get('activities') ? urlParams.get('activities').split(',') : []
                this.levels = urlParams.get('levels') ? urlParams.get('levels').split(',') : []
                this.contracts = urlParams.get('contracts') ? urlParams.get('contracts').split(',') : []
                this.cities = urlParams.get('cities') ? urlParams.get('cities').split(',') : []
                this.updateFilteredJobs()
                this.goToPage(page)
            })

            this.updateFilteredJobs()
        }
    }))
}
